'use client';

import { SsoType } from '@diritto/common/account/constants/sso.constant';
import { Color } from '@diritto/common/style/color';
import { css } from '@emotion/react';
import styled from '@emotion/styled';
import { LoginButton } from 'app-components/accounts/LoginButton';
import { LoginView } from 'app-components/accounts/LoginView';
import { ContentBodyPadding, isPC, MutableHeaderNavBarMobileHeight } from 'constants/layout';
import { ZIndex } from 'constants/zIndex';
import { useAuthState } from 'contexts/AuthProvider';
import { DirittoRoundIcon } from 'icons/DirittoRoundIcon';
import { StudioIcon } from 'icons/StudioIcon';
import Head from 'next/head';
import { usePathname } from 'next/navigation';
import React, { ReactNode, useEffect } from 'react';
import { Button } from 'ui-components/common/Button';
import { EmptyList } from 'ui-components/common/EmptyList';
import { Link } from 'ui-components/common/Link';
import { FullPageLoading } from 'ui-components/common/Loading';
import { SafeAreaBottomStyles, SafeAreaTopStyles, ViewPortMinHeight } from 'ui-components/layout/styles';
import { BackButton } from 'ui-components/navigations/HeaderButtons';
import { DirittoStudioMenuToggleButton } from 'ui-components/navigations/dirittoStudioMenu/DirittoStudioMenuToggleButton';
import { useClient } from 'ui-hooks/useClient';
import { useRequestCertificate } from 'ui-hooks/useRequestCertificate';
import { useRequestSign } from 'ui-hooks/useRequestSign';
import { useViewPort } from 'ui-hooks/useViewPort';
import { configure } from 'utils/configure';

interface HeaderLayoutProps {
  children?: ReactNode;
  leftMenu?: ReactNode;
  bottomMenu?: ReactNode;
}

export const DirittoStudioLayout = ({ children, leftMenu, bottomMenu }: HeaderLayoutProps) => {
  const isNotProduction = !configure.isProd;
  const requestCertificate = useRequestCertificate();
  const pathname = usePathname();
  const normalizedReturnUrl = pathname ?? '';
  const { isLoggedIn, isLoaded, user } = useAuthState();
  const { height: viewportHeight } = useViewPort();
  const client = useClient();
  const requestSign = useRequestSign();

  useEffect(() => {
    if (isLoaded && !isLoggedIn && client.isApp) {
      requestSign();
    }
  }, [client.isApp, isLoaded, isLoggedIn, requestSign]);

  const ogTitle = '디리토 스튜디오';
  const description = '디리토 스튜디오에서 연재를 시작해 보세요.';

  return (
    <StyledLayoutWrapper minHeight={viewportHeight}>
      <Head>
        <meta property="og:type" content="website" key="type" />
        <meta property="og:title" content={ogTitle} key="title" />
        <meta property="og:description" content={description} key="description" />
        <meta property="og:site_name" content="디리토" key="site_name" />
        <meta property="twitter:card" content="summary_large_image" key="twitter:card" />
        <meta property="twitter:site" content="@diritto_kr" key="twitter:site" />
        <meta property="twitter:title" content={ogTitle} key="twitter:title" />
        <meta property="twitter:description" content={description} key="twitter:description" />
      </Head>
      <StyledHeaderContainer>
        <StyledLogoWrapper>
          <BackButton key="back" />
          <DirittoRoundIcon className="size-[24px]" />
          <Link href="/studio" replace>
            <StudioIcon size={61} />
          </Link>
        </StyledLogoWrapper>
        <DirittoStudioMenuToggleButton />
      </StyledHeaderContainer>
      {!isLoaded && <FullPageLoading />}
      {isLoaded && !isLoggedIn && (
        <StyledLoginViewContainer>
          <LoginView title="로그인 및 회원가입" description="디리토에 오신 것을 환영해요!">
            <StyledLoginButtonWrapper>
              <LoginButton ssoType={SsoType.Kakao} normalizedReturnUrl={normalizedReturnUrl} />
              <LoginButton ssoType={SsoType.Naver} normalizedReturnUrl={normalizedReturnUrl} />
              <LoginButton ssoType={SsoType.Apple} normalizedReturnUrl={normalizedReturnUrl} />
              <LoginButton ssoType={SsoType.Google} normalizedReturnUrl={normalizedReturnUrl} />
              {isNotProduction && <LoginButton ssoType={SsoType.Email} normalizedReturnUrl={normalizedReturnUrl} />}
            </StyledLoginButtonWrapper>
          </LoginView>
        </StyledLoginViewContainer>
      )}
      {isLoaded && isLoggedIn && !user?.ageVerified && (
        <StyledEmptyList title="본인인증을 해주세요." description="해당 기능을 사용하려면 본인인증이 필요해요.">
          <StyledButton label="본인인증하러 가기" onClick={requestCertificate} />
        </StyledEmptyList>
      )}
      {isLoaded && isLoggedIn && user?.ageVerified && (
        <StyledMainWrapper>
          {leftMenu && <StyledLeftMenuWrapper minHeight={viewportHeight}>{leftMenu}</StyledLeftMenuWrapper>}
          <StyledMain>{isLoggedIn && children}</StyledMain>
        </StyledMainWrapper>
      )}
      {bottomMenu && <StyledBottomMenuWrapper>{bottomMenu}</StyledBottomMenuWrapper>}
    </StyledLayoutWrapper>
  );
};

const StyledLayoutWrapper = styled.div(
  ({ minHeight }: { minHeight: number }) => css`
    display: flex;
    flex-direction: column;
    justify-content: flex-start;
    align-items: flex-start;

    position: relative;
    width: 100%;
    ${ViewPortMinHeight(minHeight)}

    ${SafeAreaTopStyles()}
    ${SafeAreaBottomStyles()}
  `,
);

const StyledHeaderContainer = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
  align-items: center;

  position: relative;
  background-color: ${Color.White};

  width: 100%;
  height: ${MutableHeaderNavBarMobileHeight}px;

  padding: 0 ${ContentBodyPadding}px;
  border-bottom: 1px solid ${Color.Gray300};
`;

const StyledBottomMenuWrapper = styled.div`
  position: fixed;
  bottom: 0;
  left: 0;

  width: 100%;
  ${SafeAreaBottomStyles()}

  z-index: ${ZIndex.BottomNavigation};
`;

const StyledLogoWrapper = styled.div`
  display: flex;
  flex-direction: row;
  align-items: center;
  gap: 8px;
  & > a {
    display: flex;
    align-content: center;
  }
  & > button {
    margin-right: -4px;
  }
`;

const StyledMain = styled.main`
  display: flex;
  flex-direction: column;
  width: 100%;
  position: relative;
`;

const StyledMainWrapper = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: flex-start;
  flex-grow: 1;
  width: 100%;
`;

const StyledLeftMenuWrapper = styled.div(
  ({ minHeight }: { minHeight: number }) => css`
    flex-shrink: 0;
    border-right: 1px solid ${Color.Gray300};
    ${ViewPortMinHeight(minHeight - MutableHeaderNavBarMobileHeight)}
    display: none;

    @media (${isPC}) {
      display: flex;
    }
  `,
);

const StyledLoginButtonWrapper = styled.div`
  display: flex;
  flex-direction: column;
  justify-content: flex-end;
  height: 100%;
  flex-grow: 1;
  gap: 10px;
`;

const StyledLoginViewContainer = styled.div`
  display: flex;
  width: 100%;
  height: 100%;
  flex-grow: 1;
  flex-direction: column;

  @media (${isPC}) {
    justify-content: center;
    align-items: center;
  }
`;

const StyledEmptyList = styled(EmptyList)`
  margin-top: 200px;
  position: relative;
  z-index: ${ZIndex.DirittoStudioEmptyList};
`;

const StyledButton = styled(Button)`
  width: 240px;
`;
